import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Spin, message, Typography, Tag, Alert, Button, Space, Table, Tabs, Dropdown, Menu, Modal, Form, Input, Avatar, Checkbox, Select } from 'antd';
import { TeamOutlined, UserOutlined, DownOutlined, KeyOutlined, LogoutOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import isoWeek from 'dayjs/plugin/isoWeek';

import { useAuth } from '../contexts/AuthContext';
import { portalService, usuarioService } from '../services/api';
import { buildApiErrorMessage } from '../utils/apiError';
import TurmasDisponiveisList from './responsavel/TurmasDisponiveisList';
import AlunoCard from './responsavel/AlunoCard';
import SolicitarMatriculaModal from './responsavel/SolicitarMatriculaModal';
import ResponsavelFrequencia from './responsavel/ResponsavelFrequencia';
import '../styles/ResponsavelDashboard.css';

dayjs.locale('pt-br');
dayjs.extend(isoWeek);

const { Title, Text } = Typography;
const TAB_KEYS = {
  alunos: 'alunos',
  turmas: 'turmas',
  frequencia: 'frequencia',
  financeiro: 'financeiro',
  cadastro: 'cadastro',
};

const ALUNO_PORTAL_FIELDS = [
  'nome_aluno',
  'data_nascimento',
  'id_unidades',
  'status_aluno',
  'autorizacao_imagem',
];

const ALUNO_MATRICULA_REQUIRED_FIELDS = [
  { key: 'nome_aluno', label: 'Nome' },
  { key: 'id_unidades', label: 'Unidade' },
];

const RESPONSAVEL_MATRICULA_REQUIRED_FIELDS = [
  { key: 'nome_completo', label: 'Nome Completo' },
  { key: 'cpf', label: 'CPF' },
  { key: 'email', label: 'Email' },
  { key: 'telefone', label: 'Telefone' },
  { key: 'endereco', label: 'Endereço' },
  { key: 'numero', label: 'Número' },
];

const CADASTRO_ALUNO_INCOMPLETO_MSG = 'Cadastro do aluno incompleto. Revise os dados do aluno antes de solicitar a matrícula.';
const CADASTRO_RESPONSAVEL_INCOMPLETO_MSG = "Cadastro do responsável incompleto. Revise seus dados em 'Meu cadastro'.";
const AUTORIZACAO_IMAGEM_MSG = 'Para que você possa acompanhar fotos e vídeos do aluno em nossas atividades e redes sociais, é importante autorizar o uso de imagem. Deseja autorizar agora?';

const getCamposObrigatoriosFaltantes = (source, requiredFields) => {
  if (!source || typeof source !== 'object') {
    return requiredFields.map((field) => field.label);
  }

  return requiredFields
    .filter((field) => {
      const value = source[field.key];
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
    })
    .map((field) => field.label);
};

const buildCadastroIncompletoMensagem = (baseMessage, missingFields) => {
  if (!Array.isArray(missingFields) || missingFields.length === 0) return baseMessage;
  return `${baseMessage} Campos pendentes: ${missingFields.join(', ')}.`;
};

function ResponsavelDashboard() {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [alunos, setAlunos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [matriculas, setMatriculas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [faturas, setFaturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parcelasLoading, setParcelasLoading] = useState(false);
  const [error, setError] = useState('');
  const [parcelasError, setParcelasError] = useState('');
  const [modalState, setModalState] = useState({
    open: false,
    turma: null,
    alunos: [],
    inelegiveis: [],
    avisosCadastro: [],
  });
  const [creatingMatricula, setCreatingMatricula] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return Object.values(TAB_KEYS).includes(tabParam) ? tabParam : TAB_KEYS.alunos;
  });
  const [filtroAnoFinanceiro, setFiltroAnoFinanceiro] = useState(dayjs().year());
  const [filtroMesFinanceiro, setFiltroMesFinanceiro] = useState('TODOS');
  const [isSenhaModalVisible, setIsSenhaModalVisible] = useState(false);
  const [senhaLoading, setSenhaLoading] = useState(false);
  const [alunoCreateModalOpen, setAlunoCreateModalOpen] = useState(false);
  const [alunoCreateLoading, setAlunoCreateLoading] = useState(false);
  const [alunoEditModalOpen, setAlunoEditModalOpen] = useState(false);
  const [alunoEditLoading, setAlunoEditLoading] = useState(false);
  const [alunoEditing, setAlunoEditing] = useState(null);
  const [responsavelCadastro, setResponsavelCadastro] = useState(null);
  const [cadastroLoading, setCadastroLoading] = useState(false);
  const [cadastroSaving, setCadastroSaving] = useState(false);
  const [autorizacaoImagemPrompt, setAutorizacaoImagemPrompt] = useState({
    open: false,
    origem: null,
    values: null,
  });
  const [senhaForm] = Form.useForm();
  const [alunoCreateForm] = Form.useForm();
  const [alunoEditForm] = Form.useForm();
  const [cadastroForm] = Form.useForm();

  const primeiroNome = useMemo(() => {
    const nome = user?.nome_completo || user?.nome || 'Responsável';
    return nome.split(' ')[0];
  }, [user]);

  const turmasMap = useMemo(() => {
    const map = new Map();
    turmas.forEach((turma) => map.set(turma.id, turma));
    return map;
  }, [turmas]);

  const matriculasPorAluno = useMemo(() => {
    const map = new Map();
    matriculas.forEach((matricula) => {
      if (!map.has(matricula.id_alunos)) {
        map.set(matricula.id_alunos, []);
      }
      map.get(matricula.id_alunos).push(matricula);
    });
    return map;
  }, [matriculas]);

  const matriculasPendentes = useMemo(
    () => matriculas.filter((matricula) => (matricula.status || '').toUpperCase() === 'PENDENTE').length,
    [matriculas]
  );

  const parcelasVisiveis = useMemo(() => {
    return (faturas || [])
      .filter((fatura) => {
        const vencimento = dayjs(fatura.data_vencimento);
        if (!vencimento.isValid()) return false;
        if (vencimento.year() !== filtroAnoFinanceiro) return false;
        if (filtroMesFinanceiro !== 'TODOS' && vencimento.month() + 1 !== filtroMesFinanceiro) return false;
        return true;
      })
      .sort((a, b) => dayjs(a.data_vencimento).valueOf() - dayjs(b.data_vencimento).valueOf());
  }, [faturas, filtroAnoFinanceiro, filtroMesFinanceiro]);

  const anosFinanceiroOptions = useMemo(() => {
    const anoAtual = dayjs().year();
    const anos = new Set([anoAtual]);

    (faturas || []).forEach((fatura) => {
      const vencimento = dayjs(fatura.data_vencimento);
      if (vencimento.isValid()) {
        anos.add(vencimento.year());
      }
    });

    return Array.from(anos)
      .sort((a, b) => b - a)
      .map((ano) => ({ label: String(ano), value: ano }));
  }, [faturas]);

  const mesesFinanceiroOptions = useMemo(
    () => [
      { label: 'Todos os meses', value: 'TODOS' },
      { label: 'Janeiro', value: 1 },
      { label: 'Fevereiro', value: 2 },
      { label: 'Março', value: 3 },
      { label: 'Abril', value: 4 },
      { label: 'Maio', value: 5 },
      { label: 'Junho', value: 6 },
      { label: 'Julho', value: 7 },
      { label: 'Agosto', value: 8 },
      { label: 'Setembro', value: 9 },
      { label: 'Outubro', value: 10 },
      { label: 'Novembro', value: 11 },
      { label: 'Dezembro', value: 12 },
    ],
    []
  );

  const applyCadastroToForm = useCallback(
    (cadastro) => {
      if (!cadastro) return;
      cadastroForm.setFieldsValue({
        nome_completo: cadastro.nome_completo,
        cpf: cadastro.cpf,
        email: cadastro.email,
        telefone: cadastro.telefone,
        endereco: cadastro.endereco,
        numero: cadastro.numero,
        complemento: cadastro.complemento,
        bairro: cadastro.bairro,
        cep: cadastro.cep,
      });
    },
    [cadastroForm]
  );

  const fetchPortalData = useCallback(async () => {
    setLoading(true);
    setParcelasLoading(true);
    setCadastroLoading(true);
    setError('');
    setParcelasError('');

    try {
      const [alunosResult, unidadesResult, matriculasResult, turmasResult, parcelasResult, cadastroResult] = await Promise.allSettled([
        portalService.getAlunos(),
        portalService.getUnidades(),
        portalService.getMatriculas(),
        portalService.getTurmas(),
        portalService.getFaturas(),
        portalService.getMeuCadastroResponsavel(),
      ]);

      const results = [alunosResult, unidadesResult, matriculasResult, turmasResult, parcelasResult, cadastroResult];
      const failures = results.filter((result) => result.status === 'rejected').length;

      if (alunosResult.status === 'fulfilled') {
        setAlunos(alunosResult.value.data || []);
      }
      if (unidadesResult.status === 'fulfilled') {
        setUnidades(unidadesResult.value.data || []);
      }
      if (matriculasResult.status === 'fulfilled') {
        setMatriculas(matriculasResult.value.data || []);
      }
      if (turmasResult.status === 'fulfilled') {
        setTurmas(turmasResult.value.data || []);
      }
      if (parcelasResult.status === 'fulfilled') {
        setFaturas(parcelasResult.value.data || []);
      } else {
        const { message: parcelasFriendlyMessage } = buildApiErrorMessage(
          parcelasResult.reason,
          'Não foi possível carregar os dados financeiros. Tente novamente em instantes.'
        );
        setParcelasError(parcelasFriendlyMessage);
      }

      if (cadastroResult.status === 'fulfilled') {
        const cadastroData = cadastroResult.value.data || null;
        setResponsavelCadastro(cadastroData);
        applyCadastroToForm(cadastroData);
      }

      if (failures === results.length) {
        const firstFailure = results.find((result) => result.status === 'rejected');
        const { message: friendlyMessage } = buildApiErrorMessage(
          firstFailure?.reason,
          'Não foi possível carregar os dados. Tente novamente em instantes.'
        );
        setError(friendlyMessage);
        message.error(friendlyMessage);
      } else if (failures > 0) {
        message.warning('Alguns dados não puderam ser carregados agora.');
      }
    } catch (err) {
      console.error(err);
      const { message: friendlyMessage } = buildApiErrorMessage(
        err,
        'Não foi possível carregar os dados. Tente novamente em instantes.'
      );
      setError(friendlyMessage);
      message.error(friendlyMessage);
    } finally {
      setLoading(false);
      setParcelasLoading(false);
      setCadastroLoading(false);
    }
  }, [applyCadastroToForm]);

  useEffect(() => {
    fetchPortalData();
    const interval = setInterval(fetchPortalData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchPortalData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
  };

  const getParcelaStatusColor = (status) => {
    const normalized = (status || '').toUpperCase();
    if (normalized.includes('PAGO') || normalized === 'PAGA') {
      return 'green';
    }
    if (normalized.includes('ATRAS') || normalized.includes('VENC')) {
      return 'red';
    }
    return 'orange';
  };

  const handleAbrirPagamento = useCallback((url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  }, []);

  const handleCopyPix = useCallback(async (payload) => {
    if (!payload) return;
    if (!navigator?.clipboard) {
      message.warning('Copie o PIX manualmente.');
      return;
    }
    try {
      await navigator.clipboard.writeText(payload);
      message.success('PIX copiado para a área de transferência.');
    } catch (err) {
      console.error(err);
      message.error('Não foi possível copiar o PIX.');
    }
  }, []);

  const handleAbrirModal = (turma) => {
    if (!turma) return;
    const elegiveis = [];
    const inelegiveis = [];
    const avisosCadastro = [];
    const camposResponsavelFaltantes = getCamposObrigatoriosFaltantes(
      responsavelCadastro,
      RESPONSAVEL_MATRICULA_REQUIRED_FIELDS
    );
    const avisoResponsavelIncompleto = buildCadastroIncompletoMensagem(
      CADASTRO_RESPONSAVEL_INCOMPLETO_MSG,
      camposResponsavelFaltantes
    );

    alunos.forEach((aluno) => {
      const bloqueios = [];
      const avisos = [];
      const doAluno = matriculasPorAluno.get(aluno.id) || [];
      const podeMatricularNaTurma = !doAluno.some(
        (matricula) =>
          matricula.id_turma === turma.id && (matricula.status || '').toUpperCase() !== 'CANCELADA'
      );

      if (!podeMatricularNaTurma) {
        bloqueios.push('Já existe matrícula ativa ou pendente nesta turma.');
      }

      if (turma.id_unidades && aluno.id_unidades && turma.id_unidades !== aluno.id_unidades) {
        bloqueios.push('Turma não pertence à unidade do aluno.');
      }

      const camposAlunoFaltantes = getCamposObrigatoriosFaltantes(aluno, ALUNO_MATRICULA_REQUIRED_FIELDS);
      if (camposAlunoFaltantes.length > 0) {
        avisos.push(buildCadastroIncompletoMensagem(CADASTRO_ALUNO_INCOMPLETO_MSG, camposAlunoFaltantes));
      }

      if (camposResponsavelFaltantes.length > 0) {
        avisos.push(avisoResponsavelIncompleto);
      }

      if (bloqueios.length > 0) {
        inelegiveis.push({
          alunoId: aluno.id,
          alunoNome: aluno.nome_aluno || `Aluno ${aluno.id}`,
          motivos: bloqueios,
        });
        return;
      }

      elegiveis.push(aluno);

      if (avisos.length > 0) {
        avisosCadastro.push({
          alunoId: aluno.id,
          alunoNome: aluno.nome_aluno || `Aluno ${aluno.id}`,
          motivos: avisos,
        });
      }
    });

    if (elegiveis.length === 0) {
      const motivosUnicos = Array.from(new Set(inelegiveis.flatMap((item) => item.motivos || [])));
      if (motivosUnicos.length === 1) {
        message.warning(motivosUnicos[0]);
      } else {
        message.warning('Nenhum aluno elegível para esta turma. Verifique os motivos exibidos no modal.');
      }
    }

    setModalState({ open: true, turma, alunos: elegiveis, inelegiveis, avisosCadastro });
  };

  const handleFecharModal = () => {
    setModalState({ open: false, turma: null, alunos: [], inelegiveis: [], avisosCadastro: [] });
  };

  const handleSolicitarMatricula = async ({ alunoId, dia_vencimento, forma_pgto }) => {
    if (!modalState.turma) return;
    const alunoSelecionado = alunos.find((aluno) => aluno.id === alunoId);
    if (!alunoSelecionado) {
      message.error('Aluno não encontrado.');
      return;
    }

    const anoLetivoSolicitacao = String(dayjs().year());

    const payload = {
      id_alunos: alunoSelecionado.id,
      id_turma: modalState.turma.id,
      ano_letivo: anoLetivoSolicitacao,
      id_unidades: alunoSelecionado.id_unidades,
      id_serie: alunoSelecionado.id_serie ?? null,
      dia_vencimento,
      forma_pgto,
    };

    setCreatingMatricula(true);
    try {
      await portalService.createMatricula(payload);
      message.success('Solicitação enviada para análise.');
      handleFecharModal();
      fetchPortalData();
    } catch (err) {
      console.error(err);
      const { message: friendlyMessage } = buildApiErrorMessage(
        err,
        'Não foi possível enviar a solicitação. Tente novamente.'
      );
      message.error(friendlyMessage);
    } finally {
      setCreatingMatricula(false);
    }
  };

  const handleAbrirAlterarSenha = () => {
    setIsSenhaModalVisible(true);
  };

  const handleAbrirNovoAluno = () => {
    alunoCreateForm.resetFields();
    setAlunoCreateModalOpen(true);
  };

  const handleFecharNovoAluno = () => {
    setAlunoCreateModalOpen(false);
    alunoCreateForm.resetFields();
  };

  const handleSubmitNovoAluno = async (values, options = {}) => {
    if (!options.skipPrompt && (values.autorizacao_imagem === undefined || values.autorizacao_imagem === null)) {
      setAutorizacaoImagemPrompt({
        open: true,
        origem: 'novo',
        values,
      });
      return;
    }

    setAlunoCreateLoading(true);
    try {
      const response = await portalService.createAluno(values);
      const novoAluno = response?.data;

      if (novoAluno?.id) {
        setAlunos((prev) => [...prev, novoAluno]);
      }

      message.success('Aluno cadastrado com sucesso');
      handleFecharNovoAluno();
    } catch (err) {
      console.error(err);
      const { message: friendlyMessage } = buildApiErrorMessage(
        err,
        'Não foi possível cadastrar o aluno. Tente novamente.'
      );
      message.error(friendlyMessage);
    } finally {
      setAlunoCreateLoading(false);
    }
  };

  const handleAbrirEditarAluno = (aluno) => {
    if (!aluno) return;

    setAlunoEditing(aluno);
    alunoEditForm.setFieldsValue({
      nome_aluno: aluno.nome_aluno || null,
      data_nascimento: aluno.data_nascimento ? dayjs(aluno.data_nascimento).format('YYYY-MM-DD') : null,
      id_unidades: aluno.id_unidades || null,
      status_aluno: aluno.status_aluno || null,
      autorizacao_imagem: aluno.autorizacao_imagem ?? undefined,
    });
    setAlunoEditModalOpen(true);
  };

  const handleFecharEditarAluno = () => {
    setAlunoEditModalOpen(false);
    setAlunoEditing(null);
    alunoEditForm.resetFields();
  };

  const handleSubmitEditarAluno = async (values, options = {}) => {
    if (!alunoEditing?.id) return;

    if (!options.skipPrompt && (values.autorizacao_imagem === undefined || values.autorizacao_imagem === null)) {
      setAutorizacaoImagemPrompt({
        open: true,
        origem: 'editar',
        values,
      });
      return;
    }

    const payload = {};
    ALUNO_PORTAL_FIELDS.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(values, field)) {
        payload[field] = values[field];
      }
    });

    setAlunoEditLoading(true);
    try {
      const response = await portalService.updateAluno(alunoEditing.id, payload);
      const updatedAluno = response?.data;

      if (updatedAluno?.id) {
        setAlunos((prev) => prev.map((aluno) => (aluno.id === updatedAluno.id ? { ...aluno, ...updatedAluno } : aluno)));
      }

      message.success('Dados do aluno atualizados com sucesso.');
      handleFecharEditarAluno();
    } catch (err) {
      console.error(err);
      const { message: friendlyMessage } = buildApiErrorMessage(
        err,
        'Não foi possível atualizar os dados do aluno. Tente novamente.'
      );
      message.error(friendlyMessage);
    } finally {
      setAlunoEditLoading(false);
    }
  };

  const handleRespostaAutorizacaoImagem = async (resposta) => {
    const promptAtual = autorizacaoImagemPrompt;
    if (!promptAtual.open) return;

    const values = { ...(promptAtual.values || {}) };
    if (resposta === 'autorizar') {
      values.autorizacao_imagem = true;
    } else if (resposta === 'nao_autorizar') {
      values.autorizacao_imagem = false;
    }

    setAutorizacaoImagemPrompt({ open: false, origem: null, values: null });

    if (promptAtual.origem === 'novo') {
      await handleSubmitNovoAluno(values, { skipPrompt: true });
      return;
    }

    if (promptAtual.origem === 'editar') {
      await handleSubmitEditarAluno(values, { skipPrompt: true });
    }
  };

  const handleFecharAlterarSenha = () => {
    setIsSenhaModalVisible(false);
    senhaForm.resetFields();
  };

  const handleSubmitSenha = async (values) => {
    setSenhaLoading(true);
    try {
      await usuarioService.alterarMinhaSenha(values);
      message.success('Senha alterada com sucesso! Faça login novamente.');
      handleFecharAlterarSenha();
      setTimeout(() => logout(), 1200);
    } catch (error) {
      const { message: friendlyMessage } = buildApiErrorMessage(
        error,
        'Não foi possível alterar sua senha. Tente novamente.'
      );
      message.error(friendlyMessage);
    } finally {
      setSenhaLoading(false);
    }
  };

  const handleSubmitMeuCadastro = async (values) => {
    const payload = {
      nome_completo: values.nome_completo,
      cpf: (values.cpf || '').replace(/\D/g, ''),
      email: values.email,
      telefone: values.telefone || null,
      endereco: values.endereco || null,
      numero: values.numero || null,
      complemento: values.complemento || null,
      bairro: values.bairro || null,
      cep: (values.cep || '').replace(/\D/g, '') || null,
    };

    setCadastroSaving(true);
    try {
      const response = await portalService.updateMeuCadastroResponsavel(payload);
      const cadastroAtualizado = response?.data || null;
      setResponsavelCadastro(cadastroAtualizado);
      applyCadastroToForm(cadastroAtualizado);
      message.success('Cadastro atualizado com sucesso.');
    } catch (err) {
      console.error(err);
      const { message: friendlyMessage } = buildApiErrorMessage(
        err,
        'Não foi possível atualizar seu cadastro. Tente novamente.'
      );
      message.error(friendlyMessage);
    } finally {
      setCadastroSaving(false);
    }
  };

  const parcelasColumns = useMemo(
    () => [
      {
        title: 'Aluno',
        dataIndex: 'nome_aluno',
        key: 'nome_aluno',
        ellipsis: true,
      },
      {
        title: 'Turma',
        dataIndex: 'turma_nome',
        key: 'turma_nome',
        ellipsis: true,
      },
      {
        title: 'Vencimento',
        dataIndex: 'data_vencimento',
        key: 'vencimento',
        render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      },
      {
        title: 'Valor com desconto',
        dataIndex: 'valor_total',
        key: 'valor',
        align: 'right',
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Valor sem desconto após vencimento',
        dataIndex: 'valor_total_sem_desconto',
        key: 'valor_sem_desconto',
        align: 'right',
        render: (value, record) => formatCurrency(value ?? record?.valor_total),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => <Tag color={getParcelaStatusColor(status)}>{status || 'Sem status'}</Tag>,
      },
      {
        title: 'Ações',
        key: 'acoes',
        render: (_, record) => {
          const pagamentoUrl = record.invoice_url || record.bank_slip_url;
          return (
            <Space size="small">
              {pagamentoUrl && (
                <Button type="link" size="small" onClick={() => handleAbrirPagamento(pagamentoUrl)}>
                  Pagar
                </Button>
              )}
              {record.pix_copy_paste && (
                <Button type="link" size="small" onClick={() => handleCopyPix(record.pix_copy_paste)}>
                  Copiar PIX
                </Button>
              )}
            </Space>
          );
        },
      },
    ],
    [handleAbrirPagamento, handleCopyPix]
  );

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Carregando dados...</p>
      </div>
    );
  }

  const alunosTabContent = (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="primary" onClick={handleAbrirNovoAluno}>
          Novo aluno
        </Button>
      </div>

      {matriculasPendentes > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`Você possui ${matriculasPendentes} solicitação(ões) pendente(s).`}
          style={{ marginBottom: 24 }}
        />
      )}

      {alunos.length > 0 ? (
        <Row gutter={[24, 24]}>
          {alunos.map((aluno) => (
            <Col xs={24} sm={24} md={12} lg={8} key={aluno.id}>
              <AlunoCard
                aluno={aluno}
                matriculas={matriculasPorAluno.get(aluno.id) || []}
                turmasMap={turmasMap}
                onEdit={handleAbrirEditarAluno}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <Card style={{ textAlign: 'center', padding: '48px' }}>
          <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
          <Title level={4} type="secondary">
            Nenhum aluno vinculado
          </Title>
          <Text type="secondary">
            Entre em contato com a escola para vincular seus filhos à sua conta.
          </Text>
        </Card>
      )}
    </div>
  );

  const turmasTabContent = (
    <TurmasDisponiveisList
      turmas={turmas}
      loading={loading}
      onSolicitarMatricula={handleAbrirModal}
    />
  );

  const frequenciaTabContent = <ResponsavelFrequencia />;

  const financeiroTabContent = (
    <Card title="Financeiro" bordered={false} bodyStyle={{ padding: 0 }}>
      <div style={{ padding: '16px 16px 0 16px' }}>
        <Space wrap>
          <Select
            style={{ width: 160 }}
            value={filtroAnoFinanceiro}
            onChange={setFiltroAnoFinanceiro}
            options={anosFinanceiroOptions}
            placeholder="Ano"
          />
          <Select
            style={{ width: 200 }}
            value={filtroMesFinanceiro}
            onChange={setFiltroMesFinanceiro}
            options={mesesFinanceiroOptions}
            placeholder="Mês"
          />
        </Space>
      </div>
      {parcelasLoading ? (
        <div style={{ textAlign: 'center', padding: '32px' }}>
          <Spin />
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Carregando faturas...
          </Text>
        </div>
      ) : parcelasError ? (
        <Alert type="error" message={parcelasError} showIcon style={{ margin: 24 }} />
      ) : parcelasVisiveis.length === 0 ? (
        <Text type="secondary" style={{ display: 'block', padding: 24 }}>
          Nenhuma fatura encontrada para os filtros selecionados.
        </Text>
      ) : (
        <Table
          columns={parcelasColumns}
          dataSource={parcelasVisiveis}
          rowKey={(record) => record.id}
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="middle"
        />
      )}
    </Card>
  );

  const cadastroTabContent = (
    <Card title="Meu cadastro" bordered={false}>
      <Form layout="vertical" form={cadastroForm} onFinish={handleSubmitMeuCadastro}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<span>Nome completo <span style={{ color: 'red' }}>*</span></span>}
              name="nome_completo"
              rules={[{ required: true, message: 'Informe seu nome completo' }]}
              required={false}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<span>CPF <span style={{ color: 'red' }}>*</span></span>}
              name="cpf"
              rules={[
                { required: true, message: 'Informe seu CPF' },
                { len: 11, message: 'CPF deve conter 11 dígitos' },
              ]}
              required={false}
            >
              <Input
                maxLength={11}
                onChange={(event) => {
                  const onlyDigits = event.target.value.replace(/\D/g, '').slice(0, 11);
                  cadastroForm.setFieldsValue({ cpf: onlyDigits });
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<span>E-mail <span style={{ color: 'red' }}>*</span></span>}
              name="email"
              rules={[
                { required: true, message: 'Informe seu e-mail' },
                { type: 'email', message: 'E-mail inválido' },
              ]}
              required={false}
            >
              <Input type="email" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label={<span>Telefone <span style={{ color: 'red' }}>*</span></span>} name="telefone">
              <Input maxLength={20} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={16}>
            <Form.Item label={<span>Endereco <span style={{ color: 'red' }}>*</span></span>} name="endereco">
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={<span>Numero <span style={{ color: 'red' }}>*</span></span>} name="numero">
              <Input maxLength={10} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Complemento" name="complemento">
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Bairro" name="bairro">
              <Input maxLength={100} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="CEP"
          name="cep"
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const onlyDigits = String(value).replace(/\D/g, '');
                if (onlyDigits.length !== 8) {
                  return Promise.reject(new Error('CEP deve conter 8 dígitos'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            maxLength={8}
            onChange={(event) => {
              const onlyDigits = event.target.value.replace(/\D/g, '').slice(0, 8);
              cadastroForm.setFieldsValue({ cep: onlyDigits });
            }}
          />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={cadastroSaving || cadastroLoading}>
          Salvar cadastro
        </Button>
      </Form>
    </Card>
  );

  const tabsItems = [
    { key: TAB_KEYS.alunos, label: 'Alunos', children: alunosTabContent },
    {
      key: TAB_KEYS.turmas,
      label: (
        <span>
          <span className="responsavel-dashboard__tab-separator">|</span>
          Turmas
        </span>
      ),
      children: turmasTabContent,
    },
    {
      key: TAB_KEYS.frequencia,
      label: (
        <span>
          <span className="responsavel-dashboard__tab-separator">|</span>
          Frequência
        </span>
      ),
      children: frequenciaTabContent,
    },
    {
      key: TAB_KEYS.financeiro,
      label: (
        <span>
          <span className="responsavel-dashboard__tab-separator">|</span>
          Financeiro
        </span>
      ),
      children: financeiroTabContent,
    },
    {
      key: TAB_KEYS.cadastro,
      label: (
        <span>
          <span className="responsavel-dashboard__tab-separator">|</span>
          Meu cadastro
        </span>
      ),
      children: cadastroTabContent,
    },
  ];

  const profileMenu = (
    <Menu>
      <Menu.Item key="senha" icon={<KeyOutlined />} onClick={handleAbrirAlterarSenha}>
        Alterar Senha
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={logout} danger>
        Sair
      </Menu.Item>
    </Menu>
  );

  const nomeCompleto = user?.nome_completo || user?.nome || 'Responsável';

  return (
    <div className="responsavel-dashboard">
      <div className="responsavel-dashboard__header">
        <div className="responsavel-dashboard__header-main">
          <Title level={2} className="responsavel-dashboard__title">
            👋 Olá, {primeiroNome}!
          </Title>
          <Text type="secondary" className="responsavel-dashboard__subtitle">
            Acompanhe as matrículas e finanças dos seus filhos
          </Text>
        </div>

        <Dropdown overlay={profileMenu} trigger={['click']} placement="bottomRight">
          <Button className="responsavel-dashboard__user-menu" aria-label="Menu do usuário">
            <Space size={8}>
              <Avatar size="small" icon={<UserOutlined />} />
              <span className="responsavel-dashboard__user-name">{nomeCompleto}</span>
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          description="Tente novamente em instantes."
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabsItems}
        tabBarGutter={0}
        size="small"
        tabBarStyle={{ marginBottom: 24 }}
      />

      <SolicitarMatriculaModal
        open={modalState.open}
        turma={modalState.turma}
        alunos={modalState.alunos}
        inelegiveis={modalState.inelegiveis}
        avisosCadastro={modalState.avisosCadastro}
        submitting={creatingMatricula}
        onCancel={handleFecharModal}
        onSubmit={handleSolicitarMatricula}
      />

      <Modal
        title="Novo aluno"
        open={alunoCreateModalOpen}
        onCancel={handleFecharNovoAluno}
        onOk={() => alunoCreateForm.submit()}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={alunoCreateLoading}
        destroyOnClose
      >
        <Form form={alunoCreateForm} layout="vertical" onFinish={handleSubmitNovoAluno}>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label="Nome do aluno"
                name="nome_aluno"
                rules={[{ required: true, message: 'Informe o nome do aluno' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label="Unidade"
                name="id_unidades"
                rules={[{ required: true, message: 'Selecione a unidade' }]}
              >
                <Select
                  placeholder="Selecione a unidade"
                  options={unidades.map((unidade) => ({
                    label: unidade.nome_fantasia,
                    value: unidade.id,
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label="Data de nascimento"
                name="data_nascimento"
                rules={[{ required: true, message: 'Informe a data de nascimento' }]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={24}>
              <Alert
                type="info"
                showIcon
                message="Cadastro de responsáveis"
                description="Os dados de pai/mãe devem ser gerenciados na aba Meu cadastro."
              />
            </Col>
          </Row>

          <Form.Item name="autorizacao_imagem" valuePropName="checked">
            <Checkbox>Autorizacao de imagem</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Autorização de uso de imagem"
        open={autorizacaoImagemPrompt.open}
        onCancel={() => handleRespostaAutorizacaoImagem('decidir_depois')}
        footer={[
          <Button key="decidir-depois" onClick={() => handleRespostaAutorizacaoImagem('decidir_depois')}>
            Decidir depois
          </Button>,
          <Button key="nao-autorizar" onClick={() => handleRespostaAutorizacaoImagem('nao_autorizar')}>
            Não autorizar
          </Button>,
          <Button key="autorizar" type="primary" onClick={() => handleRespostaAutorizacaoImagem('autorizar')}>
            Autorizar agora
          </Button>,
        ]}
        destroyOnClose
      >
        <Text>{AUTORIZACAO_IMAGEM_MSG}</Text>
      </Modal>

      <Modal
        title={alunoEditing ? `Editar dados de ${alunoEditing.nome_aluno || 'aluno'}` : 'Editar dados do aluno'}
        open={alunoEditModalOpen}
        onCancel={handleFecharEditarAluno}
        onOk={() => alunoEditForm.submit()}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={alunoEditLoading}
        destroyOnClose
      >
        <Form form={alunoEditForm} layout="vertical" onFinish={handleSubmitEditarAluno}>
          <Form.Item
            label="Nome do aluno"
            name="nome_aluno"
            rules={[{ required: true, message: 'Informe o nome do aluno' }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Data de nascimento"
                name="data_nascimento"
                rules={[{ required: true, message: 'Informe a data de nascimento' }]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="&nbsp;">
                <Text type="secondary">Ano letivo é definido/confirmado pela administração na ativação da matrícula.</Text>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Unidade"
                name="id_unidades"
                rules={[{ required: true, message: 'Selecione a unidade' }]}
              >
                <Select
                  placeholder="Selecione a unidade"
                  options={unidades.map((unidade) => ({
                    label: unidade.nome_fantasia,
                    value: unidade.id,
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Status do aluno" name="status_aluno">
            <Select>
              <Select.Option value="ATIVO">Ativo</Select.Option>
              <Select.Option value="INATIVO">Inativo</Select.Option>
              <Select.Option value="TRANSFERIDO">Transferido</Select.Option>
              <Select.Option value="CONCLUIDO">Concluído</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="autorizacao_imagem" valuePropName="checked">
            <Checkbox>Autorização de imagem</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Alterar Senha"
        open={isSenhaModalVisible}
        onCancel={handleFecharAlterarSenha}
        footer={null}
        destroyOnClose
      >
        <Form form={senhaForm} onFinish={handleSubmitSenha} layout="vertical">
          <Form.Item
            label="Senha Atual"
            name="senha_atual"
            rules={[{ required: true, message: 'Digite sua senha atual' }]}
          >
            <Input.Password placeholder="Senha atual" />
          </Form.Item>
          <Form.Item
            label="Nova Senha"
            name="nova_senha"
            rules={[
              { required: true, message: 'Digite a nova senha' },
              { min: 6, message: 'A senha deve ter pelo menos 6 caracteres' },
            ]}
          >
            <Input.Password placeholder="Nova senha" />
          </Form.Item>
          <Form.Item
            label="Confirmar Nova Senha"
            name="confirmar_nova_senha"
            dependencies={['nova_senha']}
            rules={[
              { required: true, message: 'Confirme a nova senha' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('nova_senha') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('As senhas não coincidem'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirme a nova senha" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" block loading={senhaLoading}>
              Alterar Senha
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ResponsavelDashboard;


